import React, { useState, useRef, useEffect } from 'react';
import Banner from '../../../ui/Banner';

export default function MeetingsHeader({
  activeFilterTab,
  onFilterTabChange,
  searchQuery,
  onSearchChange,
  statusCounts = {},
  onOpenScheduleModal,
  onOpenGroupScheduleModal,
  onOpenSettingsModal,
  canCreateMeeting = true,
  isCalendarConnected,
  isGoogleCalendarConnected = false,
}) {
  const hasConnectedCalendar = Boolean(
    isCalendarConnected !== undefined ? isCalendarConnected : isGoogleCalendarConnected
  );
  const [isScheduleDropdownOpen, setIsScheduleDropdownOpen] = useState(false);
  const [showTbcModal, setShowTbcModal] = useState(false);
  const [showCalendarRequiredModal, setShowCalendarRequiredModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsScheduleDropdownOpen(false);
      }
    }
    if (isScheduleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isScheduleDropdownOpen]);

  const handleSelectOneOnOne = () => {
    setIsScheduleDropdownOpen(false);
    if (!hasConnectedCalendar) {
      setShowCalendarRequiredModal(true);
      return;
    }
    if (onOpenScheduleModal) {
      onOpenScheduleModal();
    }
  };

  const handleSelectOneToMany = () => {
    setIsScheduleDropdownOpen(false);
    if (!hasConnectedCalendar) {
      setShowCalendarRequiredModal(true);
      return;
    }
    if (onOpenGroupScheduleModal) {
      onOpenGroupScheduleModal();
    } else {
      setShowTbcModal(true);
    }
  };

  return (
    <div className="meetings-header-wrapper">
      <Banner
        title="Meeting Scheduler"
        subtitle="Manage upcoming calls, AI-assisted briefings, calendar availability, and post-meeting notes."
        type="page-header"
      />

      {/* ── Beautiful Balanced Toolbar Card ── */}
      <div className="meetings-toolbar-card">
        {/* Top: Full-Width Search & Action Buttons */}
        <div className="meetings-toolbar-top">
          <div className="meetings-search-wrap">
            <i className="fas fa-search meetings-search-icon" aria-hidden="true" />
            <input
              type="text"
              className="meetings-search-input"
              placeholder="Search meetings by guest, company, host, or subject..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="meetings-search-clear"
                onClick={() => onSearchChange('')}
                title="Clear search"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          <div className="meetings-toolbar-actions">
            <button
              type="button"
              className="meetings-btn meetings-btn-secondary"
              onClick={onOpenSettingsModal}
              title="Manage calendar sync, working hours, and buffers"
            >
              <i className="fas fa-sliders-h"></i>
              <span>Availability & Rules</span>
            </button>

            {canCreateMeeting && (
              <div className="meetings-schedule-dropdown-wrap" ref={dropdownRef}>
                <button
                  type="button"
                  className={`meetings-btn meetings-btn-primary meetings-schedule-trigger-btn ${isScheduleDropdownOpen ? 'active' : ''}`}
                  onClick={() => setIsScheduleDropdownOpen((prev) => !prev)}
                  aria-expanded={isScheduleDropdownOpen}
                  aria-haspopup="true"
                  title="Schedule a new meeting"
                >
                  <i className="fas fa-plus"></i>
                  <span>Schedule Meeting</span>
                  <i
                    className={`fas fa-chevron-down meetings-dropdown-arrow ${isScheduleDropdownOpen ? 'rotated' : ''}`}
                  ></i>
                </button>

                {isScheduleDropdownOpen && (
                  <div className="meetings-schedule-dropdown-menu" role="menu">
                    <button
                      type="button"
                      className="meetings-schedule-dropdown-item"
                      role="menuitem"
                      onClick={handleSelectOneOnOne}
                    >
                      <div className="sched-menu-icon-wrap one-one-icon">
                        <i className="fas fa-user-friends"></i>
                      </div>
                      <div className="sched-menu-text-wrap">
                        <div className="sched-menu-item-title">1-on-1 Meeting</div>
                        <div className="sched-menu-item-subtitle">
                          Individual call with client or prospect
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="meetings-schedule-dropdown-item"
                      role="menuitem"
                      onClick={handleSelectOneToMany}
                    >
                      <div className="sched-menu-icon-wrap one-many-icon">
                        <i className="fas fa-users"></i>
                      </div>
                      <div className="sched-menu-text-wrap">
                        <div className="sched-menu-item-title-row">
                          <span className="sched-menu-item-title">Group Meeting</span>
                        </div>
                        <div className="sched-menu-item-subtitle">
                          Campaign multi-lead webinars & panel sessions
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom: Modern Pill Status Filter */}
        <div className="meetings-toolbar-bottom">
          <div className="meetings-status-pills">
            {[
              { key: 'all', label: 'All Meetings', count: statusCounts.all },
              { key: 'upcoming', label: 'Upcoming', count: statusCounts.upcoming },
              { key: 'needs_recap', label: 'Needs Recap', count: statusCounts.needs_recap },
              { key: 'completed', label: 'Completed', count: statusCounts.completed },
              { key: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`meetings-pill-btn ${activeFilterTab === tab.key ? 'active' : ''}`}
                onClick={() => onFilterTabChange(tab.key)}
              >
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className="meetings-pill-count">{tab.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── TBC Information Modal for Group Meeting ── */}
      {showTbcModal && (
        <div className="meetings-modal-overlay" onClick={() => setShowTbcModal(false)}>
          <div
            className="meetings-modal-container meetings-tbc-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="meetings-modal-header">
              <div className="meetings-modal-header-left">
                <div className="meetings-tbc-badge-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div>
                  <h2 className="meetings-modal-title">Group & Multi-Attendee Meeting</h2>
                  <p className="meetings-modal-subtitle">
                    Webinars, panel presentations & multi-guest sessions
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="meetings-modal-close-btn"
                onClick={() => setShowTbcModal(false)}
                aria-label="Close dialog"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="meetings-modal-body meetings-tbc-modal-body">
              <div className="meetings-tbc-notice-box">
                <div className="meetings-tbc-status-pill">
                  <i className="fas fa-clock"></i> In Development (TBC)
                </div>
                <h3>Group Meeting Scheduling is Coming Soon</h3>
                <p>
                  We are currently developing advanced capabilities for group bookings, multi-lead
                  discovery webinars, collective host round-robin, and co-host assignments.
                </p>
                <div className="meetings-tbc-highlights">
                  <div className="tbc-highlight-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Multiple attendee registration with capacity limits</span>
                  </div>
                  <div className="tbc-highlight-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Group calendar slot synchronization</span>
                  </div>
                  <div className="tbc-highlight-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Automated multi-guest reminder broadcasts</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="meetings-modal-footer">
              <button
                type="button"
                className="meetings-btn meetings-btn-primary"
                onClick={() => setShowTbcModal(false)}
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Individual Calendar Connection Required Modal ── */}
      {showCalendarRequiredModal && (
        <div className="meetings-modal-overlay" onClick={() => setShowCalendarRequiredModal(false)}>
          <div
            className="meetings-modal-container meetings-calendar-required-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="meetings-modal-header">
              <div className="meetings-modal-header-left">
                <div className="meetings-calendar-req-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div>
                  <h2 className="meetings-modal-title">Individual Calendar Required</h2>
                  <p className="meetings-modal-subtitle">
                    Connect your personal Google or Microsoft account to enable 1-on-1 scheduling
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="meetings-modal-close-btn"
                onClick={() => setShowCalendarRequiredModal(false)}
                aria-label="Close dialog"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="meetings-modal-body meetings-calendar-req-body">
              <div className="meetings-calendar-req-notice">
                <div className="meetings-cal-status-pill warning">
                  <i className="fas fa-exclamation-triangle"></i> Individual Calendar Not Connected
                </div>
                <h3>Connect Your Individual Calendar</h3>
                <p>
                  Calendar connections in Lead Huntrix are individual-based. Each manager and member
                  connects their own Google Calendar or Microsoft Calendar to:
                </p>
                <ul className="meetings-cal-req-features">
                  <li>
                    <i className="fas fa-check-circle"></i>
                    <span>
                      <strong>Generate valid video meeting links</strong> (Google Meet or Microsoft
                      Teams) hosted directly from your own account
                    </span>
                  </li>
                  <li>
                    <i className="fas fa-check-circle"></i>
                    <span>
                      <strong>Sync your individual availability</strong> and prevent double booking
                      conflicts
                    </span>
                  </li>
                  <li>
                    <i className="fas fa-check-circle"></i>
                    <span>
                      <strong>Send instant calendar invitations</strong> to guests from your
                      calendar
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="meetings-modal-footer meetings-cal-req-footer">
              <button
                type="button"
                className="meetings-btn meetings-btn-outline"
                onClick={() => setShowCalendarRequiredModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="meetings-btn meetings-btn-primary"
                onClick={() => {
                  setShowCalendarRequiredModal(false);
                  if (onOpenSettingsModal) {
                    onOpenSettingsModal();
                  }
                }}
              >
                <i className="fas fa-calendar-plus"></i>
                <span>Connect Calendar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
